import React, { useState, useEffect, useRef } from 'react';
import { 
  SaveOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import ImageUpload from './ImageUpload';
import { apiClient } from '../../lib/api';
import type { Section, Image } from '../../lib/api';

interface SectionFormProps {
  section?: Section | null;
  onSave: (sectionData: Partial<Section>) => Promise<void>;
  onCancel: () => void;
  onDelete?: (id: string) => Promise<void>;
  isLoading?: boolean;
}

const SectionForm: React.FC<SectionFormProps> = ({
  section,
  onSave,
  onCancel,
  onDelete,
  isLoading = false
}) => {
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    longDescription: '',
    isPublished: false,
    isVideo: false,
    metadata: '{}',
  });

  const [images, setImages] = useState<Image[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  // Ref to track if images are already loaded for current section
  const loadedSectionRef = useRef<string | null>(null);

  // Initialize form data when section changes
  useEffect(() => {
    if (section) {
      setFormData({
        title: section.title || '',
        shortDescription: section.shortDescription || '',
        longDescription: section.longDescription || '',
        isPublished: section.isPublished || false,
        isVideo: section.isVideo || false,
        metadata: JSON.stringify(section.metadata || {}, null, 2),
      });
      setImages(section.images || []);
      
      // Load images for existing section
      if (section.id && section.id !== loadedSectionRef.current) {
        loadedSectionRef.current = section.id;
        loadSectionImages(section.id);
      }
    } else {
      // Reset for new section
      setFormData({
        title: '',
        shortDescription: '',
        longDescription: '',
        isPublished: false,
        isVideo: false,
        metadata: '{}',
      });
      setImages([]);
      loadedSectionRef.current = null;
    }
    setErrors({});
  }, [section]);

  // Load images for a section
  const loadSectionImages = async (sectionId: string) => {
    // Prevent duplicate requests
    if (isLoadingImages) {
      console.log('Images already loading, skipping request');
      return;
    }
    
    try {
      setIsLoadingImages(true);
      console.log('Loading images for section:', sectionId);
      const response = await apiClient.getImages({ sectionId });
      if (response.data) {
        console.log('Loaded images:', response.data.length);
        setImages(response.data);
      }
    } catch (error: any) {
      console.warn('Failed to load images:', error);
      // Don't show error to user for image loading failures
      // Set empty array to prevent infinite retries
      setImages([]);
    } finally {
      setIsLoadingImages(false);
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (formData.shortDescription.length > 200) {
      newErrors.shortDescription = 'Short description must be 200 characters or less';
    }

    // Validate metadata JSON
    if (formData.metadata.trim()) {
      try {
        JSON.parse(formData.metadata);
      } catch (e) {
        newErrors.metadata = 'Metadata must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const sectionData: Partial<Section> = {
        title: formData.title.trim(),
        shortDescription: formData.shortDescription.trim() || undefined,
        longDescription: formData.longDescription.trim() || undefined,
        isPublished: formData.isPublished,
        isVideo: formData.isVideo,
        metadata: formData.metadata.trim() ? JSON.parse(formData.metadata) : {},
      };

      await onSave(sectionData);
    } catch (error: any) {
      console.error('Save error:', error);
      setErrors({ submit: error.message || 'Failed to save section' });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!section?.id || !onDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${section.title}"? This action cannot be undone.`)) {
      try {
        await onDelete(section.id);
      } catch (error: any) {
        setErrors({ submit: error.message || 'Failed to delete section' });
      }
    }
  };

  // Image management methods
  const handleImageUpload = async (files: File[], metadata: any[]) => {
    if (!section?.id) {
      setErrors({ submit: 'Please save the section first before uploading images' });
      return;
    }

    setIsLoadingImages(true);
    try {
      console.log('🚀 Uploading images with metadata:', metadata.map(m => ({ 
        title: m.title, 
        width: m.width, 
        height: m.height 
      })));

      const result = await apiClient.uploadImages(section.id, files, {
        titles: metadata.map(m => m.title),
        descriptions: metadata.map(m => m.description),
        altTexts: metadata.map(m => m.altText),
        widths: metadata.map(m => m.width || null),
        heights: metadata.map(m => m.height || null)
      });

      if (result.success) {
        setImages(prev => [...prev, ...result.data]);
        console.log('✅ Images uploaded successfully with dimensions');
      }
    } catch (error: any) {
      console.error('❌ Image upload failed:', error);
      setErrors({ submit: error.message || 'Failed to upload images' });
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleImageDelete = async (imageId: string) => {
    try {
      await apiClient.deleteImage(imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to delete image' });
    }
  };

  const handleImageReorder = async (imageIds: string[]) => {
    if (!section?.id) return;
    
    try {
      await apiClient.reorderImages(section.id, imageIds);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to reorder images' });
    }
  };

  const handleSetFeaturedImage = async (imageId: string | null) => {
    if (!section?.id) return;
    
    try {
      await apiClient.setFeaturedImage(section.id, imageId);
      // Update the section's featured image in local state
      if (imageId) {
        // Setting a new featured image
        const selectedImage = images.find(img => img.id === imageId);
        if (selectedImage) {
          // Update the section object with new featured image info
          Object.assign(section, {
            featuredImageId: imageId,
            featuredImage: {
              url: selectedImage.url,
              alt: selectedImage.alt || selectedImage.title || ''
            }
          });
        }
      } else {
        // Removing featured image
        Object.assign(section, {
          featuredImageId: null,
          featuredImage: null
        });
      }
      // Trigger re-render
      setFormData(prev => ({ ...prev }));
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to set featured image' });
    }
  };

  const isEditing = !!section?.id;
  const characterCount = {
    title: formData.title.length,
    shortDescription: formData.shortDescription.length,
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="admin-card mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onCancel}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeftOutlined className="mr-2" />
              Back to Sections
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-9">
                {isEditing ? 'Edit Section' : 'Create New Section'}
              </h1>
              {isEditing && (
                <p className="text-sm text-gray-6 mt-1">
                  Last updated: {new Date(section.updatedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          
          {isEditing && (
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                section.isPublished 
                  ? 'bg-success-50 text-success-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {section.isPublished ? 'Published' : 'Draft'}
              </span>
              {onDelete && (
                <button
                  onClick={handleDelete}
                  className="flex items-center px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <DeleteOutlined className="mr-1" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="admin-card">
            <h2 className="text-lg font-semibold text-gray-9 mb-4">Basic Information</h2>
            
            {/* Title */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium text-gray-7 mb-2">
                Title *
                <span className="text-xs text-gray-5 ml-2">
                  ({characterCount.title}/100)
                </span>
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg transition-colors ${
                  errors.title 
                    ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                    : 'border-gray-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
                }`}
                placeholder="Enter section title"
                maxLength={100}
                disabled={isSaving}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Short Description */}
            <div className="mb-6">
              <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-7 mb-2">
                Short Description
                <span className="text-xs text-gray-5 ml-2">
                  (for card display, {characterCount.shortDescription}/200)
                </span>
              </label>
              <textarea
                id="shortDescription"
                value={formData.shortDescription}
                onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg transition-colors resize-none ${
                  errors.shortDescription 
                    ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                    : 'border-gray-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
                }`}
                placeholder="Brief description for content cards"
                rows={3}
                maxLength={200}
                disabled={isSaving}
              />
              {errors.shortDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
              )}
            </div>

            {/* Long Description */}
            {/* <div>
              <label htmlFor="longDescription" className="block text-sm font-medium text-gray-7 mb-2">
                Long Description
                <span className="text-xs text-gray-5 ml-2">
                  (for detail pages)
                </span>
              </label>
              <textarea
                id="longDescription"
                value={formData.longDescription}
                onChange={(e) => handleInputChange('longDescription', e.target.value)}
                className="w-full px-3 py-2 border border-gray-4 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                placeholder="Detailed description for section detail pages"
                rows={6}
                disabled={isSaving}
              />
            </div> */}
          </div>

          {/* Publishing Options */}
          <div className="admin-card">
            <h2 className="text-lg font-semibold text-gray-9 mb-4">Publishing Options</h2>
            
            <div className="space-y-4">
              {/* Published Toggle */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  {formData.isPublished ? (
                    <EyeOutlined className="text-green-600" />
                  ) : (
                    <EyeInvisibleOutlined className="text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-7">
                      Publication Status
                    </p>
                    <p className="text-xs text-gray-5">
                      {formData.isPublished 
                        ? 'Currently visible on public website' 
                        : 'Currently hidden from public website'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('isPublished', false)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      !formData.isPublished 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={isSaving}
                  >
                    OFF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isPublished', true)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      formData.isPublished 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={isSaving}
                  >
                    ON
                  </button>
                </div>
              </div>

              {/* Video Toggle */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-primary-500">
                    📹
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-7">
                      Video Content
                    </p>
                    <p className="text-xs text-gray-5">
                      {formData.isVideo 
                        ? 'Section contains video content' 
                        : 'Section contains images only'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('isVideo', false)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      !formData.isVideo 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={isSaving}
                  >
                    OFF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isVideo', true)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      formData.isVideo 
                        ? 'bg-primary-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={isSaving}
                  >
                    ON
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Metadata (Advanced) */}
          {/* <div className="admin-card">
            <h2 className="text-lg font-semibold text-gray-9 mb-2">Advanced Settings</h2>
            <p className="text-sm text-gray-6 mb-4">
              Additional metadata in JSON format (tags, location, date, etc.)
            </p>
            
            <textarea
              value={formData.metadata}
              onChange={(e) => handleInputChange('metadata', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg font-mono text-sm transition-colors resize-none ${
                errors.metadata 
                  ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                  : 'border-gray-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
              }`}
              placeholder='{"tags": ["expedition", "faroe-islands"], "location": "Faroe Islands"}'
              rows={4}
              disabled={isSaving}
            />
            {errors.metadata && (
              <p className="mt-1 text-sm text-red-600">{errors.metadata}</p>
            )}
          </div> */}

          {/* Image Management */}
          <div className="admin-card">
            <h2 className="text-lg font-semibold text-gray-9 mb-2">Images</h2>
            <p className="text-sm text-gray-6 mb-4">
              Upload and manage images for this section
            </p>
            
            {isEditing ? (
              <ImageUpload
                images={images}
                onImagesChange={setImages}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                onReorder={handleImageReorder}
                onSetFeatured={handleSetFeaturedImage}
                featuredImageId={section?.featuredImageId}
                isLoading={isLoadingImages}
                maxFiles={50}
              />
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  💡 Save the section first to enable image uploads
                </p>
              </div>
            )}
          </div>

          {/* Form Errors */}
          {errors.submit && (
            <div className="admin-card bg-red-50 border border-red-200">
              <p className="text-red-700">{errors.submit}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="admin-card">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  type="submit"
                  disabled={isSaving || isLoading}
                  className="flex items-center px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
                >
                  {(isSaving || isLoading) ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <SaveOutlined className="mr-2" />
                      {isEditing ? 'Update Section' : 'Create Section'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SectionForm;